/**
 * useBundleEditor
 * ---------------
 * BundleBuilder-flavored adapter around the shared, schema-agnostic
 * useVariantEditor engine (src/hooks/useVariantEditor.ts) — supplies the
 * bundle Zod resolver and SKIP_KEYS, everything else (variant cache, A/B
 * enable/add/remove, payload/override building) lives in the shared engine.
 * Mirrors VolumeDiscount/hooks/useBundleEditor.ts exactly.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import type { Resolver } from "react-hook-form";
import { BundleSchema } from "./bundle.schema";
import type { BundleBuilderConfigInterface } from "../../Types";
import { useVariantEditor, type UseVariantEditorReturn } from "../../../../hooks/useVariantEditor";

export { BASE_TARGET } from "../../../../hooks/useVariantEditor";
export type { EditorVariant } from "../../../../hooks/useVariantEditor";

/** Keys that are ALWAYS taken from base, regardless of which variant is active. */
export const SKIP_KEYS: (keyof BundleBuilderConfigInterface)[] = [
    "offer_name",
    "order_discounts",
    "product_discounts",
    "shipping_discounts",
    "bundle_builder_link",
    "start_date",
    "end_date",
    "targeting",
    "general_settings",
    "bundles_settings",
    "swatchOptions",
    "ab_config",
    "title",
    "bundle_as_product",
];

export interface UseBundleEditorOptions {
    initialValues: BundleBuilderConfigInterface;
    /**
     * Called when the user clicks "Save".
     * Receives the complete payload ready to send to the backend.
     */
    onSave: (payload: BundleBuilderConfigInterface) => Promise<void>;
}

export function useBundleEditor({ initialValues, onSave }: UseBundleEditorOptions) {
    return useVariantEditor<BundleBuilderConfigInterface>({
        initialValues,
        resolver: zodResolver(BundleSchema) as unknown as Resolver<BundleBuilderConfigInterface>,
        skipKeys: SKIP_KEYS,
        onSave,
    });
}

export type UseBundleEditorReturn = UseVariantEditorReturn<BundleBuilderConfigInterface>;
